import { useState, useMemo, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  UserCircleIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';

/* ─── Password requirements ─── */
const PASSWORD_RULES = [
  { key: 'req_length', test: (p: string) => p.length >= 8 },
  { key: 'req_uppercase', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'req_lowercase', test: (p: string) => /[a-z]/.test(p) },
  { key: 'req_number', test: (p: string) => /\d/.test(p) },
  { key: 'req_special', test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
] as const;

export default function UserSettingsPage() {
  const { t } = useTranslation(['profile', 'common']);
  const { user } = useAuth();

  /* ─── Personal Info ─── */
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [phone, setPhone] = useState('');

  /* Sync state when user data loads asynchronously */
  useEffect(() => {
    if (user) {
      setFirstName((prev) => prev || user.firstName || '');
      setLastName((prev) => prev || user.lastName || '');
    }
  }, [user]);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  /* ─── Password ─── */
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const userRole = useMemo(() => {
    if (!user) return 'User';
    if (user.isSuperuser) return 'Administrator';
    if (user.isStaff) return 'Staff';
    return 'Dentist';
  }, [user]);

  const passwordValidation = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(newPassword) })),
    [newPassword],
  );

  const isPasswordValid = useMemo(
    () =>
      passwordValidation.every((r) => r.passed) &&
      newPassword === confirmPassword &&
      currentPassword.length > 0,
    [passwordValidation, newPassword, confirmPassword, currentPassword],
  );

  /* ─── Handlers ─── */
  const handleSaveProfile = useCallback(async () => {
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const res = await fetch('/api/me/', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: firstName, last_name: lastName, phone_number: phone }),
      });
      if (res.ok) {
        setProfileMsg({ type: 'success', text: t('personalInfo.updateSuccess') });
      } else {
        const data = await res.json().catch(() => null);
        setProfileMsg({
          type: 'error',
          text: data?.detail || t('personalInfo.updateError'),
        });
      }
    } catch {
      setProfileMsg({ type: 'error', text: t('personalInfo.updateError') });
    } finally {
      setProfileSaving(false);
      setTimeout(() => setProfileMsg(null), 4000);
    }
  }, [firstName, lastName, phone, t]);

  const handleChangePassword = useCallback(async () => {
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: t('security.mismatch') });
      return;
    }
    setPasswordSaving(true);
    setPasswordMsg(null);
    try {
      const res = await fetch('/api/change-password/', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      if (res.ok) {
        setPasswordMsg({ type: 'success', text: t('security.changeSuccess') });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await res.json().catch(() => null);
        setPasswordMsg({
          type: 'error',
          text: data?.detail || t('security.changeError'),
        });
      }
    } catch {
      setPasswordMsg({ type: 'error', text: t('security.changeError') });
    } finally {
      setPasswordSaving(false);
      setTimeout(() => setPasswordMsg(null), 4000);
    }
  }, [currentPassword, newPassword, confirmPassword, t]);

  /* ─── Shared classes ─── */
  const inputCls =
    'w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200';
  const disabledCls =
    'w-full px-4 py-2.5 rounded-xl border border-border bg-muted text-muted-foreground cursor-not-allowed';
  const labelCls = 'block text-sm font-medium text-foreground mb-1.5';

  return (
    <>
      <Helmet>
        <title>{t('common:settings', { defaultValue: 'Account Settings' })} - Doctif</title>
      </Helmet>

      <div className="max-w-4xl mx-auto space-y-8">
        {/* ─── Header ─── */}
        <div className="flex items-center gap-4">
          <Link
            to="/settings"
            className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
          >
            <ArrowLeftIcon className="h-4.5 w-4.5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              {t('common:settings', { defaultValue: 'Account Settings' })}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">{t('subtitle')}</p>
          </div>
        </div>

        {/* ═══════════════════════════════════════════
            PERSONAL INFORMATION — editable
        ═══════════════════════════════════════════ */}
        <section className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <UserCircleIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">{t('personalInfo.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('personalInfo.description')}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Alert */}
            {profileMsg && (
              <Alert type={profileMsg.type} text={profileMsg.text} />
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>{t('personalInfo.firstName')}</label>
                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t('personalInfo.lastName')}</label>
                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputCls} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>{t('personalInfo.email')}</label>
                <input type="email" value={user?.email || ''} disabled className={disabledCls} />
              </div>
              <div>
                <label className={labelCls}>{t('personalInfo.phone')}</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('personalInfo.phonePlaceholder')}
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>{t('personalInfo.role')}</label>
                <input type="text" value={userRole} disabled className={disabledCls} />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveProfile}
                disabled={profileSaving}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 focus:ring-offset-card disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {profileSaving ? t('personalInfo.saving') : t('personalInfo.saveChanges')}
              </button>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            SECURITY & PASSWORD
        ═══════════════════════════════════════════ */}
        <section className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <KeyIcon className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">{t('security.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('security.description')}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {passwordMsg && <Alert type={passwordMsg.type} text={passwordMsg.text} />}

            {/* Current password */}
            <div>
              <label className={labelCls}>{t('security.currentPassword')}</label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={`${inputCls} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showCurrent ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* New + Confirm */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>{t('security.newPassword')}</label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`${inputCls} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showNew ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className={labelCls}>{t('security.confirmPassword')}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-200 ${
                    confirmPassword && newPassword !== confirmPassword
                      ? 'border-destructive focus:border-destructive'
                      : 'border-border focus:border-primary'
                  }`}
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="mt-1.5 text-xs text-destructive">{t('security.mismatch')}</p>
                )}
              </div>
            </div>

            {/* Password strength */}
            {newPassword.length > 0 && (
              <div className="bg-muted/50 rounded-xl p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  {t('security.passwordRequirements')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {passwordValidation.map((rule) => (
                    <div key={rule.key} className="flex items-center gap-2">
                      {rule.passed ? (
                        <CheckCircleIcon className="h-4 w-4 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircleIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <span
                        className={`text-xs ${rule.passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}
                      >
                        {t(`security.${rule.key}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                onClick={handleChangePassword}
                disabled={!isPasswordValid || passwordSaving}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:ring-offset-2 focus:ring-offset-card disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <KeyIcon className="h-4 w-4" />
                {passwordSaving ? t('security.changing') : t('security.changePassword')}
              </button>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            DANGER ZONE
        ═══════════════════════════════════════════ */}
        <section className="rounded-2xl border border-destructive/30 bg-card overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-destructive" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-foreground">{t('dangerZone.title')}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{t('dangerZone.deleteWarning')}</p>
              </div>
              <button
                disabled
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl border border-destructive/30 text-destructive font-medium text-sm hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex-shrink-0"
              >
                {t('dangerZone.deleteAccount')}
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

/* ─── Inline Alert component ─── */
function Alert({ type, text }: { type: 'success' | 'error'; text: string }) {
  return (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
        type === 'success'
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
          : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
      }`}
    >
      {type === 'success' ? (
        <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
      ) : (
        <XCircleIcon className="h-4 w-4 flex-shrink-0" />
      )}
      {text}
    </div>
  );
}
